import { useState, useMemo, type ReactNode } from "react";
import { ChevronDown, Search, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";

export interface FilterOptionObject {
  value: string;
  label: string;
  icon?: ReactNode;
  depth?: number;
  childValues?: string[];
  parentId?: string;
}

export type FilterOption = string | FilterOptionObject;

const getOptionValue = (opt: FilterOption) => (typeof opt === "string" ? opt : opt.value);
const getOptionLabel = (opt: FilterOption) => (typeof opt === "string" ? opt : opt.label);
const getOptionIcon = (opt: FilterOption) => (typeof opt === "string" ? undefined : opt.icon);

export interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  "aria-label"?: string;
  /** Trigger height — g360's `size` variant. `"sm"` is what the advanced filter
   * drawers use; `"md"` is the default for the filter bars. Note `"sm"` is the
   * TALLER of the two, which inverts g360's h-8: per user direction, a stack of
   * ~21 facets reads better as roomy rows — 48px tall, with the label padded off
   * the field's edges rather than filling it. Sized by padding (`min-h`, not a
   * fixed `h`) so a facet name that wraps grows the row instead of spilling. */
  size?: "sm" | "md";
  surface?: "card" | "input";
  /** Extra classes on the trigger button — e.g. `"w-full"` so every field
   * in a vertically-stacked filter drawer shares one consistent width. */
  className?: string;
  popoverClassName?: string;
  /** Set when this dropdown lives inside a Sheet/Dialog. The popover portals to
   * `document.body`, i.e. outside the Sheet's subtree, so the Sheet's
   * react-remove-scroll lock cancels wheel events over the option list (only
   * dragging the scrollbar still worked). Going modal registers the popover's
   * own lock on top, restoring wheel scroll. Leave off elsewhere — modal also
   * locks page scroll and blocks outside pointer events, which the filter-bar
   * dropdowns don't want. */
  modal?: boolean;
  trigger?: ReactNode;
  clearLabel?: string;
  /** When set, at least this many options must remain selected.
   * Prevents unselecting the last remaining option(s) or clearing all. */
  minSelected?: number;
  /** "pill" uses the toolbar filter border (grey, indigo on hover, no blue when selected). */
  variant?: "default" | "pill";
}

/** g360's `SEARCH_AUTO_THRESHOLD`: at or above this many options the in-popover
 * search appears automatically. Below it, scrolling beats searching. */
const SEARCH_AUTO_THRESHOLD = 3;

/** Multi-select facet dropdown — trigger shows the facet name + a count
 * badge, popover holds a search box (for longer lists) and a checkbox
 * list with Select All / Clear. Used for Plugin Family / Group /
 * Environment across the IT Compliance filter bars. */
export function FilterDropdown({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  loading = false,
  searchable,
  searchPlaceholder,
  "aria-label": ariaLabel,
  size = "md",
  surface = "card",
  className,
  popoverClassName = "w-64",
  modal = false,
  trigger,
  minSelected,
  variant = "default",
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const showSearch = searchable ?? options.length >= SEARCH_AUTO_THRESHOLD;

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();

    const matchSet = new Set<string>();
    options.forEach((o) => {
      const lbl = getOptionLabel(o).toLowerCase();
      const val = getOptionValue(o);
      if (lbl.includes(q)) {
        matchSet.add(val);
      }
    });

    return options.filter((o) => {
      const val = getOptionValue(o);
      if (matchSet.has(val)) return true;
      if (typeof o !== "string") {
        if (o.childValues?.some((cv) => matchSet.has(cv))) return true;
        if (o.parentId && matchSet.has(o.parentId)) return true;
      }
      return false;
    });
  }, [options, query]);

  const parentValues = useMemo(() => {
    const set = new Set<string>();
    options.forEach((o) => {
      if (typeof o !== "string" && o.childValues && o.childValues.length > 0) {
        set.add(getOptionValue(o));
      }
    });
    return set;
  }, [options]);

  const displayCount = useMemo(() => {
    if (parentValues.size === 0) return value.length;
    return value.filter((v) => !parentValues.has(v)).length;
  }, [parentValues, value]);

  const isActive = displayCount > 0;

  const toggle = (option: FilterOption) => {
    const optionVal = getOptionValue(option);
    const childValues = typeof option === "string" ? undefined : option.childValues;
    const parentId = typeof option === "string" ? undefined : option.parentId;
    const hasChildren = !!(childValues && childValues.length > 0);

    let next: string[];

    if (hasChildren) {
      const allRelated = [optionVal, ...childValues!];
      const allSelected = allRelated.every((v) => value.includes(v));

      if (allSelected) {
        // Deselect parent and all its children
        next = value.filter((v) => !allRelated.includes(v));
      } else {
        // Select parent and all its children
        next = Array.from(new Set([...value, ...allRelated]));
      }
    } else {
      // Leaf node or single item
      const isSelected = value.includes(optionVal);
      next = isSelected ? value.filter((v) => v !== optionVal) : [...value, optionVal];

      // If this child belongs to a parent, update parent's selected state
      if (parentId) {
        const parentOpt = options.find(
          (o) => typeof o !== "string" && o.value === parentId,
        ) as FilterOptionObject | undefined;

        if (parentOpt?.childValues) {
          const siblings = parentOpt.childValues;
          const allSiblingsSelected = siblings.every((sib) => next.includes(sib));
          if (allSiblingsSelected) {
            if (!next.includes(parentId)) next.push(parentId);
          } else {
            next = next.filter((v) => v !== parentId);
          }
        }
      }
    }

    if (minSelected !== undefined && minSelected > 0) {
      const nextCount = parentValues.size === 0 ? next.length : next.filter((v) => !parentValues.has(v)).length;
      if (nextCount < minSelected) {
        return;
      }
    }

    onChange(next);
  };

  const handleSelectAll = () => {
    const allVals = new Set<string>();
    filtered.forEach((o) => {
      allVals.add(getOptionValue(o));
      if (typeof o !== "string" && o.childValues) {
        o.childValues.forEach((cv) => allVals.add(cv));
      }
    });
    onChange(Array.from(allVals));
  };

  const effectiveSelectedCount = parentValues.size === 0 ? value.length : displayCount;

  const isClearDisabled =
    value.length === 0 ||
    (minSelected !== undefined && minSelected > 0 && effectiveSelectedCount <= minSelected);

  const clearTitle =
    isClearDisabled && minSelected !== undefined && minSelected > 0 && value.length > 0
      ? minSelected === 1
        ? "At least one option must be selected"
        : `At least ${minSelected} options must be selected`
      : undefined;

  const handleClear = () => {
    if (minSelected !== undefined && minSelected > 0) {
      if (options.length === 0) return;
      const selectedInOptionsOrder = options
        .map(getOptionValue)
        .filter((v) => value.includes(v));
      const nextSelected =
        selectedInOptionsOrder.length >= minSelected
          ? selectedInOptionsOrder.slice(0, minSelected)
          : options.slice(0, minSelected).map(getOptionValue);

      const allVals = new Set<string>();
      nextSelected.forEach((val) => {
        allVals.add(val);
        const opt = options.find((o) => getOptionValue(o) === val);
        if (opt && typeof opt !== "string" && opt.childValues) {
          opt.childValues.forEach((cv) => allVals.add(cv));
        }
      });
      onChange(Array.from(allVals));
      return;
    }
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <button
            type="button"
            disabled={disabled || loading}
            aria-label={ariaLabel ?? `${label} filter`}
            data-active={isActive || undefined}
            className={`inline-flex items-center justify-between gap-1.5 rounded-md border ${surface === "input" ? "bg-input-background" : "bg-card"} whitespace-nowrap transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-60 ${size === "sm" ? "min-h-12 px-4 py-3 text-sm" : "h-10 px-3 text-sm"
              } ${variant === "pill"
              ? "border-border hover:border-indigo-400/60 focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20"
              : `${isActive ? "border-blue-600 dark:border-blue-500" : "border-border"} hover:border-blue-500/60 dark:hover:border-blue-400/60`
              } ${className ?? ""}`}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="font-medium truncate">{placeholder && !isActive ? placeholder : label}</span>
              {isActive && (
                <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                  {displayCount}
                </span>
              )}
            </span>
            {loading ? (
              <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className={`${popoverClassName} p-0`}>
        {showSearch && (
          <div className="flex items-center gap-1.5 border-b border-border px-2.5 py-2">
            <Search className="size-3.5 text-muted-foreground shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder ?? `Search ${label}...`}
              className="flex-1 bg-transparent text-sm outline-none"
            />
          </div>
        )}
        <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs font-medium text-foreground hover:opacity-80"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={isClearDisabled}
            title={clearTitle}
            className="text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground text-center">No options</p>
          ) : (
            filtered.map((option) => {
              const val = getOptionValue(option);
              const lbl = getOptionLabel(option);
              const icon = getOptionIcon(option);
              const depth = typeof option === "string" ? 0 : (option.depth ?? 0);
              const childValues = typeof option === "string" ? undefined : option.childValues;
              const hasChildren = !!(childValues && childValues.length > 0);

              const allChildrenSelected = hasChildren && childValues!.every((cv) => value.includes(cv));
              const someChildrenSelected = hasChildren && childValues!.some((cv) => value.includes(cv));
              const selfSelected = value.includes(val);

              const isChecked = hasChildren ? allChildrenSelected : selfSelected;
              const isIndeterminate = hasChildren && !isChecked && (someChildrenSelected || selfSelected);

              const isLocked = Boolean(
                minSelected !== undefined &&
                minSelected > 0 &&
                options.length > 0 &&
                isChecked &&
                effectiveSelectedCount <= minSelected
              );

              return (
                <label
                  key={val}
                  style={{ paddingLeft: `${depth * 18 + 8}px` }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent/60 cursor-pointer"
                >
                  {depth > 0 && (
                    <span className="text-muted-foreground/40 text-xs select-none -mr-0.5">└</span>
                  )}
                  <input
                    type="checkbox"
                    className={isLocked ? "cursor-not-allowed" : "cursor-pointer"}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    checked={isChecked}
                    disabled={isLocked}
                    onChange={() => {
                      if (!isLocked) toggle(option);
                    }}
                  />
                  <span className="truncate flex-1" title={lbl}>
                    {lbl}
                  </span>
                  {icon}
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
